import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { IoCheckmark, IoCopyOutline, IoDocumentTextOutline, IoLinkOutline } from 'react-icons/io5';

import { getServiceId } from '@/api/tokens.helper.ts';
import { DateTimePicker, Divider, FilePicker, SegmentedControl } from '@/components';
import { Button, ButtonOutlined, ButtonOutlinedError } from '@/components/Button';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import { DocumentPreviewModal } from '@/components/DocumentPreview/DocumentPreview';
import { Autocomplete, Checkbox, Input, Select, TextArea } from '@/components/Inputs';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import { formatRole } from '@/components/Widget/WhosOnBoardWidget/WhosOnBoardWidget.constants';
import { useCrewManagement } from '@/hooks/useCrewManagement';
import { FRONTEND_FEATURES } from '@/permissions/frontend-features';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { useFrontendFeature } from '@/permissions/useFrontendFeature';
import { usePermissions } from '@/permissions/usePermissions';
import {
  useDocumentByIdQuery,
  useUploadFinancialDocumentsFileMutation,
} from '@/store/documents/documents.store.ts';
import { useAllEquipmentQuery } from '@/store/equipment/equipment.store';
import { useInvoice } from '@/store/expenses-store/invoices/invoices.store.ts';
import type {
  CreateTransactionBody,
  Transaction,
  TransactionCategory,
  TransactionMethodType,
} from '@/store/expenses-store/transactions/transactions.dto.ts';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store.ts';
import { useServiceProvidersQuery } from '@/store/service-providers/service-providers.store.ts';
import { capitalize } from '@/utils/capitalize.ts';
import {
  formatAmountDisplay,
  moneyInputProps,
  parseAmount,
  sanitizeAmount,
  toAmountString,
} from '@/utils/form';
import { formatCurrency } from '@/utils/formatCurrency.ts';
import { OWNER_GROUP_ROLES } from '@/utils/roles';
import { toast } from '@/utils/toast.ts';
import { useRoles } from '@hooks/useRoles';
import { ExpenseEquipmentField } from '@pages/Expenses/components/ExpenseEquipmentField.tsx';
import {
  type CrewRecipient,
  crewRecipientLabel,
  crewRecipientOf,
  toCrewSnapshot,
} from '@pages/Expenses/TransactionsPage/utils/crewRecipient';

import { buildPaymentBody } from './buildPaymentBody';
import { CategoryModal, DEFAULT_CATEGORY_COLOR } from './CategoryModal';
import { ModalOrPanel } from './ModalOrPanel';
import { openPaymentCheckout, paymentReturnUrl } from './openPaymentCheckout';
import styles from './TransactionModal.module.scss';

type TransactionModalProps = {
  open: boolean;
  onClose: () => void;
  expense?: Transaction | null;
  initialFields?: Pick<Transaction, 'invoice_id' | 'amount'> & {
    service_provider_id?: string;
    company_name?: string;
  };
  lockInvoice?: boolean;
  lockedCategoryId?: TransactionCategory;
  onSuccess?: () => void;
  submitLabel?: string;
  title?: string;
  isExist?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  canManage?: boolean;
  startInEdit?: boolean;
};

// const EXPENSE_STATUSES: ExpenseStatus[] = [
//   'pending',
//   'processing',
//   'completed',
//   'failed',
//   'cancelled',
// ];
const RECIPIENT_GROUPS = { providers: 'Service providers', crew: 'Crew' } as const;

const METHOD_TYPE_OPTIONS: { label: string; value: TransactionMethodType }[] = [
  { label: 'Cash', value: 'cash' },
  { label: 'Bank Transfer', value: 'bank_transfer' },
  { label: 'Credit Card', value: 'credit_card' },
];

const TransactionModal = ({
  open,
  onClose,
  expense,
  // lockInvoice,
  lockedCategoryId,
  onSuccess,
  submitLabel = 'Save',
  isExist,
  title,
  onEdit,
  onDelete,
  canManage = true,
  startInEdit,
}: TransactionModalProps) => {
  const [isTemporarilyClosed, setIsTemporarilyClosed] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(!!startInEdit);
  const [previewDocument, setPreviewDocument] = useState<{
    contentType: string;
    name: string;
    storageLink: string;
  } | null>(null);

  const { removeTransaction, setTransactionStatus } = useTransactionsStore();
  const [statusUpdating, setStatusUpdating] = useState(false);

  const isPendingApproval = expense?.status === 'pending' || expense?.status === 'needs_approval';
  const paymentLink = expense?.payment_link ?? null;
  const { hasAnyRole } = useRoles();
  const canPay = useFrontendFeature(FRONTEND_FEATURES.PaymentsUI);
  // Approving opens the payment checkout, so it is part of paying.
  const canApprove = canPay && isPendingApproval && canManage && hasAnyRole(OWNER_GROUP_ROLES);

  const handleApprove = () => {
    if (!paymentLink) return;
    void openPaymentCheckout(paymentLink, () => {
      handleClose();
      onSuccess?.();
    });
  };

  const handleStatusChange = async (status: 'completed' | 'cancelled') => {
    if (!expense?.id) return;
    setStatusUpdating(true);
    try {
      await setTransactionStatus(expense.id, status);
      handleClose();
      onSuccess?.();
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleOpenPreview = (doc: { contentType: string; name: string; storageLink: string }) => {
    setIsTemporarilyClosed(true);
    setPreviewDocument(doc);
  };

  const handleClosePreview = () => {
    setPreviewDocument(null);
    setIsTemporarilyClosed(false);
  };

  const handleDeleteClick = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (expense?.id) {
      await removeTransaction(expense.id);
      onClose();
      onDelete?.();
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    onEdit?.();
  };

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  const showForm = canManage && (!isExist || isEditing);

  return (
    <>
      <ModalOrPanel
        open={open && !isTemporarilyClosed && !isDeleteConfirmOpen}
        onClose={handleClose}
        title={
          title ?? (isEditing ? 'Edit Transaction' : expense ? 'Transaction' : 'Add Transaction')
        }
        badge={
          isPendingApproval && !isEditing ? (
            <StatusPill label="Pending Approval" tone="warning" />
          ) : undefined
        }
        footer={
          canApprove && paymentLink && !isEditing ? (
            <>
              <Button disabled={statusUpdating} onClick={handleApprove}>
                Approve
              </Button>
              <ButtonOutlinedError
                disabled={statusUpdating}
                onClick={() => void handleStatusChange('cancelled')}
              >
                Reject
              </ButtonOutlinedError>
            </>
          ) : undefined
        }
        haveActions={isExist && !isEditing && canManage}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      >
        {showForm ? (
          <NewTransaction
            // lockInvoice={lockInvoice}
            lockedCategoryId={!isEditing ? lockedCategoryId : undefined}
            onSuccess={onSuccess}
            submitLabel={isEditing ? 'Update' : submitLabel}
            onClose={handleClose}
            expense={isEditing ? expense : undefined}
          />
        ) : (
          <ExistingTransaction
            expense={expense}
            onPreviewDocument={handleOpenPreview}
            paymentLink={canApprove ? paymentLink : null}
          />
        )}
      </ModalOrPanel>
      {previewDocument && (
        <DocumentPreviewModal document={previewDocument} onClose={handleClosePreview} />
      )}
      <ConfirmationModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Transaction"
        message="Are you sure you want to delete this transaction?"
      />
    </>
  );
};

export default TransactionModal;

type ExistingTransactionProps = Omit<TransactionModalProps, 'open' | 'isExist' | 'onClose'> & {
  onPreviewDocument: (doc: { contentType: string; name: string; storageLink: string }) => void;
  paymentLink?: string | null;
};

const PaymentLinkField = ({ url }: { url: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Couldn’t copy the link. Please try again.');
    }
  };

  return (
    <div className={styles.paymentLinkField}>
      <IoLinkOutline className={styles.paymentLinkIcon} />
      <span className={styles.paymentLinkUrl} title={url}>
        {url}
      </span>
      <button
        type="button"
        className={clsx(styles.paymentLinkCopy, copied && styles.paymentLinkCopied)}
        onClick={handleCopy}
      >
        {copied ? <IoCheckmark /> : <IoCopyOutline />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

const ExistingTransaction = ({
  expense,
  onPreviewDocument,
  paymentLink,
}: ExistingTransactionProps) => {
  const { invoice, getInvoice } = useInvoice();
  const { transactionCategories, getTransactionsCategories } = useTransactionsStore();
  const vesselId = getServiceId();
  const spQuery = useServiceProvidersQuery(100, { vesselId: vesselId, search: '' });

  const serviceProviders = spQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const recipientId =
    typeof expense?.recipient === 'string' ? expense?.recipient : expense?.recipient?.id;
  const recipientLabel =
    typeof expense?.recipient === 'string' ? undefined : expense?.recipient?.name;
  const crewRecipient = crewRecipientOf(expense);
  const merchantName = crewRecipient
    ? crewRecipientLabel(crewRecipient)
    : (serviceProviders.find((sp) => sp.id === recipientId)?.name ??
      recipientLabel ??
      recipientId ??
      '');

  const { data: invoiceDocument } = useDocumentByIdQuery(invoice?.document_id);

  useEffect(() => {
    if (!expense?.invoice_id) {
      useInvoice.setState({ invoice: null });
      return;
    }
    void getInvoice(expense.invoice_id);
  }, [expense?.invoice_id, getInvoice]);

  useEffect(() => {
    return () => {
      useInvoice.setState({ invoice: null });
    };
  }, []);

  useEffect(() => {
    void getTransactionsCategories();
  }, [getTransactionsCategories]);

  const handleInvoiceClick = () => {
    if (invoiceDocument) {
      onPreviewDocument({
        contentType: invoiceDocument.contentType,
        name: invoiceDocument.name,
        storageLink: invoiceDocument.storageLink,
      });
    }
  };

  return (
    <div className={styles.existingTransactions}>
      <div className={styles.heading}>
        <p className={styles.date}>
          {new Date(expense?.created_at as string).toLocaleDateString('en-US')}
        </p>
        <p className={styles.amount}>
          {formatCurrency(parseAmount(expense?.amount.number ?? '0'))} {expense?.amount.currency}
        </p>
      </div>
      <div className={styles.recipientEquipment}>
        <div>
          <span>Recipient</span>
          <span>{merchantName}</span>
          <Divider className={styles.modalDivider} />
        </div>
        <div>
          <span>Category</span>
          <span>
            {transactionCategories.find(({ id }) => id === expense?.expenses_category)?.title}
          </span>
          <Divider className={styles.modalDivider} />
        </div>
      </div>
      <div className={styles.typeMethod}>
        <div>
          <span>Type</span>
          <span>{capitalize(expense?.type as string)}</span>
          <Divider className={styles.modalDivider} />
        </div>
        <div>
          <span>Method</span>
          <span>{capitalize(expense?.method as string)}</span>
          <Divider className={styles.modalDivider} />
        </div>
      </div>
      {/*Uncomment after adding new field about stripe payment info*/}
      {/*{expense?.description && (*/}
      {/*  <div className={styles.fieldGroup}>*/}
      {/*    <span>Description</span>*/}
      {/*    <span>{expense?.description}</span>*/}
      {/*    <Divider className={styles.modalDivider} />*/}
      {/*  </div>*/}
      {/*)}*/}
      {expense && expense.equipments.length > 0 && (
        <div className={styles.fieldGroup}>
          <span>Equipment</span>
          {expense.equipments.map((equipment) => (
            <span key={equipment.id}>{equipment.name}</span>
          ))}
          <Divider className={styles.modalDivider} />
        </div>
      )}
      {invoice && (
        <div
          className={styles.invoiceCard}
          onClick={handleInvoiceClick}
          style={{ cursor: invoiceDocument ? 'pointer' : 'default' }}
        >
          <IoDocumentTextOutline />
          <div>
            <p>{invoice.invoice_number}</p>
            <p>{invoice.company_name}</p>
          </div>
        </div>
      )}
      {paymentLink && <PaymentLinkField url={paymentLink} />}
    </div>
  );
};

type NewTransactionProps = Omit<
  TransactionModalProps,
  'open' | 'initialFields' | 'isExist' | 'lockInvoice'
>;

const NewTransaction = ({
  // lockInvoice,
  lockedCategoryId,
  onSuccess,
  submitLabel,
  onClose,
  expense,
}: NewTransactionProps) => {
  const {
    createTransaction,
    createTransactionCategory,
    updateTransaction,
    createPayment,
    getAllTransactions,
    loading,
    transactionCategories,
    getTransactionsCategories,
  } = useTransactionsStore();
  const { data: equipmentList = [] } = useAllEquipmentQuery();
  const { mutateAsync: uploadFinancialFile } = useUploadFinancialDocumentsFileMutation();
  // const invoices = useInvoice((state) => state.invoices);
  // const getInvoices = useInvoice((state) => state.getInvoices);
  const vesselId = getServiceId();
  const spQuery = useServiceProvidersQuery(100, { vesselId, search: '' });
  const serviceProviders = spQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const { can } = usePermissions();
  // The right the category endpoints ask for, and the one the budget page gates its own
  // category actions on: Owner and Captain hold it, crew do not.
  const canManageCategories = can(PERMISSIONS.wallet.TransactionsBasic);

  const isEditing = !!expense;

  // An edit always saves through the manual branch — the update call lives there, and
  // the payment branch would send a new payment instead — so it opens on that form.
  const [chosenMode, setMode] = useState<'manual' | 'payment'>(isEditing ? 'manual' : 'payment');
  // Without payments there is only the manual form, whatever the toggle last held.
  const canPay = useFrontendFeature(FRONTEND_FEATURES.PaymentsUI);
  const mode = canPay ? chosenMode : 'manual';
  const [needApproval, setNeedApproval] = useState(false);
  const { hasAnyRole } = useRoles();
  const isOwner = hasAnyRole(OWNER_GROUP_ROLES);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectErrors, setSelectErrors] = useState({
    amount: '',
    currency: '',
    category: '',
    recipient: '',
    chosenDate: '',
  });

  const [amount, setAmount] = useState(sanitizeAmount(String(expense?.amount?.number ?? '')));
  const [currency, setCurrency] = useState(expense?.amount?.currency ?? 'USD');
  const [chosenDate, setChosenDate] = useState(
    expense?.created_at
      ? new Date(expense.created_at).toLocaleDateString('en-US')
      : new Date().toLocaleDateString('en-US'),
  );
  const [methodType, setMethodType] = useState<TransactionMethodType>(
    (expense?.method_type as TransactionMethodType) ?? 'cash',
  );
  const [description, setDescription] = useState(expense?.description ?? '');
  const initialCrewRecipient = crewRecipientOf(expense);
  // A crew member's id is not a provider's: it opens as the crew pick, not as a provider id.
  const initialRecipientId = initialCrewRecipient
    ? ''
    : typeof expense?.recipient === 'string'
      ? (expense?.recipient ?? '')
      : (expense?.recipient?.id ?? '');
  const initialRecipientName =
    typeof expense?.recipient === 'string' ? '' : (expense?.recipient?.name ?? '');
  const [recipientId, setRecipientId] = useState(initialRecipientId);
  const [recipientName, setRecipientName] = useState(initialRecipientName);
  // A custom name opens as the picked value, not as search text — the clear cross would
  // otherwise leave it behind as the recipient.
  const [recipientSearch, setRecipientSearch] = useState(
    initialRecipientId ? initialRecipientName : '',
  );
  /** A typed name confirmed as the recipient — shown in the picker the way a picked provider is. */
  const [customRecipient, setCustomRecipient] = useState(
    initialRecipientId || initialCrewRecipient ? '' : initialRecipientName,
  );
  const [crewRecipient, setCrewRecipient] = useState<CrewRecipient | null>(initialCrewRecipient);
  // The vessel's current crew only — owners, providers, guests and anyone removed are not in it.
  const { crewMembers } = useCrewManagement();
  const crewOptions: CrewRecipient[] = crewMembers.map((member) => ({
    id: member.id,
    name: [member.firstName, member.lastName].filter(Boolean).join(' '),
    role: formatRole(member.services.find(({ service_id }) => service_id === vesselId)?.role ?? ''),
  }));
  const [categoryId, setCategoryId] = useState(
    lockedCategoryId?.id ?? expense?.expenses_category ?? '',
  );
  const [categorySearch, setCategorySearch] = useState(lockedCategoryId?.title ?? '');
  /** Non-null while the inline create dialog is up; the string seeds its name field. */
  const [newCategoryName, setNewCategoryName] = useState<string | null>(null);
  // const [invoiceId, setInvoiceId] = useState(expense?.invoice_id ?? '');
  // const [invoiceSearch, setInvoiceSearch] = useState('');

  const [selectedEquipmentId, setSelectedEquipmentId] = useState(
    expense?.equipments?.[0]?.id ?? '',
  );
  const [receiptId, setReceiptId] = useState(expense?.receipt ?? '');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // const invoiceFileInputRef = useRef<HTMLInputElement | null>(null);
  // const [invoiceDocId, setInvoiceDocId] = useState('');
  // const [invoiceFileName, setInvoiceFileName] = useState('');
  // const [isInvoiceUploading, setIsInvoiceUploading] = useState(false);

  // useEffect(() => {
  //   void getInvoices();
  // }, [getInvoices]);

  useEffect(() => {
    void getTransactionsCategories();
  }, [getTransactionsCategories]);

  const resetForm = () => {
    setPaymentUrl(null);
    setNeedApproval(false);
    setSelectErrors({
      amount: '',
      currency: '',
      category: '',
      recipient: '',
      chosenDate: '',
    });
    setAmount('');
    setCurrency('USD');
    setMethodType('cash');
    setChosenDate(new Date().toLocaleDateString('en-US'));
    setDescription('');
    setRecipientId('');
    setRecipientName('');
    setRecipientSearch('');
    setCustomRecipient('');
    setCrewRecipient(null);
    setCategoryId('');
    setCategorySearch('');
    // setInvoiceId('');
    // setInvoiceSearch('');
    setSelectedEquipmentId('');
    setReceiptId('');
    setReceiptFile(null);
    // setInvoiceDocId('');
    // setInvoiceFileName('');
  };

  const handleModeChange = (nextMode: 'manual' | 'payment') => {
    setMode(nextMode);
    setSelectErrors({
      amount: '',
      currency: '',
      category: '',
      recipient: '',
      chosenDate: '',
    });
  };

  // const handleInvoiceUploadClick = () => {
  //   invoiceFileInputRef.current?.click();
  // };

  // const handleInvoiceFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files?.[0];
  //   event.target.value = '';
  //   if (!file) return;
  //   setIsInvoiceUploading(true);
  //   try {
  //     const response = await uploadDocumentsFile({ file, parentId: null });
  //     const responseData = response?.data as { id?: string } | { id?: string }[] | undefined;
  //     const uploadedId = Array.isArray(responseData) ? responseData[0]?.id : responseData?.id;
  //
  //     setInvoiceDocId(uploadedId ?? '');
  //     setInvoiceFileName(file.name);
  //     setInvoiceId('');
  //     setInvoiceSearch('');
  //   } finally {
  //     setIsInvoiceUploading(false);
  //   }
  // };

  // The category the picker currently points at: the one that was selected, or an existing
  // one whose title the free-typed text spells out. Keyed on the id, never the slug —
  // wallet reads and writes the id, a rename rewrites the slug, and a title with no latin
  // characters slugs to an empty string, which an unset picker would then match and pin to
  // the field for good.
  const findMatchedCategory = () => {
    if (categoryId) return transactionCategories.find(({ id }) => id === categoryId);

    const normalizedTypedCategory = categorySearch.trim().toLowerCase();
    if (!normalizedTypedCategory) return undefined;

    return transactionCategories.find(
      ({ title }) => title.toLowerCase() === normalizedTypedCategory,
    );
  };

  const categoryError = () => {
    if (!categoryId && !categorySearch.trim()) return 'Required';
    // Text naming no existing category turns into a new one on save, which sits behind the
    // same right as the Add row — without it, only a pick from the list will do.
    if (!canManageCategories && !findMatchedCategory())
      return 'Please select a category from the list.';

    return '';
  };

  // Shared by both modes: the matched category's id, or one created on the fly from the
  // free-typed search text.
  const resolveCategoryId = async () => {
    const typedCategory = categorySearch.trim();
    const matchedCategory = findMatchedCategory();

    if (!matchedCategory && typedCategory && canManageCategories) {
      const createdCategory = await createTransactionCategory(
        typedCategory,
        DEFAULT_CATEGORY_COLOR,
      );

      return createdCategory.id;
    }

    return matchedCategory?.id ?? categoryId;
  };

  const handlePaymentSubmit = async () => {
    const sp = serviceProviders.find((s) => s.id === recipientId);

    const nextSelectErrors = {
      amount: !amount
        ? 'Required'
        : Number(amount) <= 0
          ? 'Please enter an amount greater than 0.'
          : '',
      currency: '',
      category: categoryError(),
      recipient: '',
      chosenDate: '',
    };

    if (!recipientId) {
      nextSelectErrors.recipient = 'Please select a recipient.';
    } else if (!sp?.contact_info?.email) {
      nextSelectErrors.recipient =
        'This provider has no email on file. Please choose another recipient.';
    }

    setSelectErrors(nextSelectErrors);

    const hasErrors = Object.values(nextSelectErrors).some(Boolean);
    if (hasErrors) {
      return;
    }

    if (!sp) {
      return;
    }

    const resolvedCategoryId = await resolveCategoryId();

    const returnUrl = paymentReturnUrl();
    const body = buildPaymentBody({
      amount,
      recipientEmail: sp.contact_info.email,
      recipientName: sp.name,
      category: resolvedCategoryId,
      description,
      successUrl: returnUrl,
      cancelUrl: returnUrl,
      needApproval: isOwner ? undefined : needApproval,
    });

    setIsSubmitting(true);
    try {
      const url = await createPayment(body);

      if (url) {
        setPaymentUrl(url);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (mode === 'payment') {
      await handlePaymentSubmit();
      return;
    }

    const nextSelectErrors = {
      amount: amount ? '' : 'Required',
      currency: currency ? '' : 'Required',
      category: categoryError(),
      recipient:
        recipientId || crewRecipient || recipientName.trim() || recipientSearch.trim()
          ? ''
          : 'Required',
      chosenDate: chosenDate ? '' : 'Required',
    };

    setSelectErrors(nextSelectErrors);

    const hasErrors = Object.values(nextSelectErrors).some(Boolean);
    if (hasErrors) {
      return;
    }

    const selectedEquipment = equipmentList.find((e) => e.id === selectedEquipmentId);
    const equipments = selectedEquipment
      ? [{ id: selectedEquipment.id, name: selectedEquipment.name }]
      : undefined;

    const recipientPayload = crewRecipient
      ? { id: crewRecipient.id, name: crewRecipient.name }
      : recipientId
        ? {
            id: recipientId,
            name:
              recipientName ||
              serviceProviders.find((sp) => sp.id === recipientId)?.name ||
              recipientSearch.trim(),
          }
        : {
            name: recipientName.trim() || recipientSearch.trim(),
          };

    const expensesCategory = await resolveCategoryId();

    let finalReceiptId = receiptId;

    if (receiptFile) {
      setIsUploading(true);
      try {
        const uploadRes = await uploadFinancialFile({ file: receiptFile });
        const uploadData = uploadRes?.data as { id?: string } | { id?: string }[] | undefined;
        finalReceiptId = (Array.isArray(uploadData) ? uploadData[0]?.id : uploadData?.id) ?? '';
      } finally {
        setIsUploading(false);
      }
    }

    const body: CreateTransactionBody = {
      type: 'expenses',
      amount: {
        number: toAmountString(amount),
        currency,
      },
      expenses_category: expensesCategory,
      recipient: recipientPayload,
      method: 'manual',
      method_type: methodType,
      description: description || undefined,
      // invoice_id: invoiceDocId || invoiceId || undefined,
      equipments,
      receipt: finalReceiptId || undefined,
      // Sent whole on every save: wallet's update replaces `detail` rather than merging it.
      detail: {
        ...expense?.detail,
        recipient: crewRecipient ? toCrewSnapshot(crewRecipient) : undefined,
      },
      created_at: new Date(chosenDate).toISOString(),
      // A manual entry records money that already moved, so it lands settled rather than
      // awaiting approval. Wallet only falls back to 'pending' when status is left off the
      // create body; its update endpoint ignores status, so this reads as create-only.
      status: 'completed',
    };

    if (isEditing && expense?.id) {
      await updateTransaction(expense.id, body);
    } else {
      await createTransaction(body);
    }
    resetForm();
    onClose();
    onSuccess?.();
  };

  if (paymentUrl) {
    const finish = () => {
      resetForm();
      onClose();
      onSuccess?.();
    };

    // The settled transaction is created by a Stripe webhook when payment finishes, which
    // can land after the browser closes — a single refetch would race it. Poll until the
    // new row appears (or give up after the window; a later page visit re-syncs).
    const pollForSettled = async (prevCount: number, tries = 6) => {
      for (let i = 0; i < tries; i++) {
        await getAllTransactions();
        if (useTransactionsStore.getState().transactions.length > prevCount) return;
        // ponytail: fixed 1.5s backoff (~9s window), fine for a webhook that lands in seconds
        await new Promise((r) => setTimeout(r, 1500));
      }
    };

    return (
      <div className={styles.paymentSuccess}>
        <div className={styles.paymentSuccessBadge}>
          <IoCheckmark />
        </div>
        <div className={styles.paymentSuccessText}>
          <h3 className={styles.paymentSuccessHeading}>Payment Link Ready</h3>
          <p className={styles.paymentSuccessSubtext}>
            Please share this link with the recipient to collect the payment.
          </p>
        </div>

        <PaymentLinkField url={paymentUrl} />

        <div className={styles.paymentSuccessActions}>
          <ButtonOutlined
            type="button"
            onClick={() => {
              const prevCount = useTransactionsStore.getState().transactions.length;
              openPaymentCheckout(paymentUrl, () => {
                finish();
                void pollForSettled(prevCount);
              });
            }}
          >
            Open Link
          </ButtonOutlined>
          <Button type="button" onClick={finish}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <form className={styles.form} onSubmit={handleSubmit}>
        {!isEditing && canPay && (
          <SegmentedControl
            className={styles.modeToggle}
            value={mode}
            onChange={handleModeChange}
            options={[
              { label: 'Send Payment', value: 'payment' },
              { label: 'Record Manually', value: 'manual' },
            ]}
          />
        )}
        <div className={styles.row}>
          <Input
            className={styles.wide}
            label="Amount"
            placeholder="0.00"
            suffix="$"
            {...moneyInputProps}
            value={formatAmountDisplay(amount)}
            onChange={(e) => {
              setAmount(sanitizeAmount(e.target.value));
              if (selectErrors.amount) {
                setSelectErrors((prev) => ({ ...prev, amount: '' }));
              }
            }}
            mandatory
            error={selectErrors.amount}
          />
          {mode === 'manual' && (
            <div className={styles.selectWrapper}>
              <Select
                label="Method"
                value={methodType}
                onChange={(value) => setMethodType(value as TransactionMethodType)}
                options={METHOD_TYPE_OPTIONS}
                placeholder="Method"
              />
            </div>
          )}
        </div>
        <div className={styles.selectWrapper}>
          <Autocomplete
            label="Recipient"
            value={recipientSearch}
            onChange={(value) => {
              setRecipientSearch(value);
              setRecipientName(value);
              if (recipientId) setRecipientId('');
              setCrewRecipient(null);
              if (selectErrors.recipient) {
                setSelectErrors((prev) => ({ ...prev, recipient: '' }));
              }
            }}
            onItemSelect={(value) => {
              const selectedId = value ?? '';
              const crew = crewOptions.find(({ id }) => id === selectedId);
              if (crew) {
                setCrewRecipient(crew);
                setRecipientId('');
                setRecipientName(crew.name);
                setRecipientSearch('');
                setCustomRecipient('');
                if (selectErrors.recipient) {
                  setSelectErrors((prev) => ({ ...prev, recipient: '' }));
                }
                return;
              }
              setCrewRecipient(null);
              const serviceProvider = serviceProviders.find((s) => s.id === selectedId);
              setRecipientId(selectedId);
              setRecipientName(serviceProvider?.name ?? recipientSearch);
              setRecipientSearch(serviceProvider?.name ?? recipientSearch);
              setCustomRecipient('');
              if (selectErrors.recipient) {
                setSelectErrors((prev) => ({ ...prev, recipient: '' }));
              }
            }}
            // Only a manual entry takes a name outside the providers: a payment goes to a
            // provider's email on file, which a typed name does not have.
            onNewOption={
              mode === 'manual'
                ? (typed) => {
                    const name = typed.trim().slice(0, 255);
                    if (!name) return;
                    setRecipientId('');
                    setCrewRecipient(null);
                    setRecipientName(name);
                    setRecipientSearch('');
                    setCustomRecipient(name);
                    if (selectErrors.recipient) {
                      setSelectErrors((prev) => ({ ...prev, recipient: '' }));
                    }
                  }
                : undefined
            }
            newOptionLabel={(typed) => `Use "${typed}"`}
            selectedOptionLabel={
              recipientId
                ? (serviceProviders.find((sp) => sp.id === recipientId)?.name ?? null)
                : crewRecipient
                  ? crewRecipientLabel(crewRecipient)
                  : customRecipient || null
            }
            options={[
              ...serviceProviders
                .filter((sp) => sp.name.toLowerCase().includes(recipientSearch.toLowerCase()))
                .map((sp) => ({ label: sp.name, value: sp.id, group: RECIPIENT_GROUPS.providers })),
              // A payment goes to a provider's email on file, which crew are not paid through.
              ...(mode === 'manual'
                ? crewOptions
                    .filter(({ name }) =>
                      name.toLowerCase().includes(recipientSearch.toLowerCase()),
                    )
                    .map((crew) => ({
                      label: crewRecipientLabel(crew),
                      value: crew.id,
                      group: RECIPIENT_GROUPS.crew,
                    }))
                : []),
            ]}
            groups={
              mode === 'manual'
                ? [
                    { name: RECIPIENT_GROUPS.providers },
                    {
                      name: RECIPIENT_GROUPS.crew,
                      // Only for a vessel with nobody on it; a search that matches no one just
                      // leaves the group out.
                      empty: crewOptions.length === 0 ? 'No crew on this vessel yet' : undefined,
                    },
                  ]
                : undefined
            }
            error={selectErrors.recipient}
            placeholder="Chantier Naval Azur"
            mandatory
          />
        </div>
        <div className={clsx(styles.row, styles.stackOnMobile)}>
          <div className={clsx(styles.selectWrapper, styles.wide)}>
            <Autocomplete
              label="Category"
              disabled={!!lockedCategoryId}
              value={categorySearch}
              onChange={(value) => {
                setCategorySearch(value);
                if (categoryId) {
                  setCategoryId('');
                }
                if (selectErrors.category) {
                  setSelectErrors((prev) => ({ ...prev, category: '' }));
                }
              }}
              onItemSelect={(value) => {
                setCategoryId(value ?? '');
                if (value) {
                  const selectedCategory = transactionCategories.find(({ id }) => id === value);
                  if (selectedCategory) {
                    setCategorySearch(selectedCategory.title);
                  }
                }
                if (selectErrors.category) {
                  setSelectErrors((prev) => ({ ...prev, category: '' }));
                }
              }}
              selectedOptionLabel={
                (categoryId && transactionCategories.find(({ id }) => id === categoryId)?.title) ||
                null
              }
              options={transactionCategories
                .filter(({ title }) => title.toLowerCase().includes(categorySearch.toLowerCase()))
                .map(({ id, title }) => ({ label: title, value: id }))}
              onNewOption={
                canManageCategories ? (typed) => setNewCategoryName(typed.trim()) : undefined
              }
              alwaysOfferNewOption={canManageCategories}
              newOptionLabel={(typed) => (
                <>
                  <FiPlus size={14} />
                  {typed ? `Add “${typed}”` : 'Add Category'}
                </>
              )}
              error={selectErrors.category}
              placeholder="Dockage"
              mandatory
            />
          </div>
          {mode === 'manual' && (
            <DateTimePicker
              label="Transaction Date"
              placeholder="Jul 18, 2026"
              value={chosenDate}
              onChange={(v) => {
                setChosenDate(v);
                if (selectErrors.chosenDate) {
                  setSelectErrors((prev) => ({ ...prev, chosenDate: '' }));
                }
              }}
              mandatory
              error={selectErrors.chosenDate}
            />
          )}
        </div>
        {mode === 'manual' && (
          <div className={clsx(styles.row, styles.stackOnMobile)}>
            <ExpenseEquipmentField
              className={styles.wide}
              value={selectedEquipmentId}
              onChange={setSelectedEquipmentId}
            />
            <FilePicker
              label="Receipt"
              placeholder="Upload Receipt"
              file={receiptFile}
              loading={isUploading}
              // A stored receipt is known by id alone; nothing hands its file name back.
              fileName={receiptId ? 'Receipt attached' : undefined}
              onPick={(file) => {
                setReceiptFile(file);
                setReceiptId('');
              }}
              onClear={() => {
                setReceiptFile(null);
                setReceiptId('');
              }}
            />
          </div>
        )}
        {/*<div className={styles.selectWithUpload}>*/}
        {/*  <Autocomplete*/}
        {/*    label="Choose the Invoice"*/}
        {/*    disabled={lockInvoice || !!invoiceDocId}*/}
        {/*    value={invoiceSearch}*/}
        {/*    onChange={setInvoiceSearch}*/}
        {/*    onItemSelect={(value) => {*/}
        {/*      setInvoiceId(value ?? '');*/}
        {/*      setInvoiceDocId('');*/}
        {/*      setInvoiceFileName('');*/}
        {/*    }}*/}
        {/*    selectedOptionLabel={*/}
        {/*      invoiceFileName*/}
        {/*        ? invoiceFileName*/}
        {/*        : invoices.find((inv) => inv.id === invoiceId)*/}
        {/*          ? `${invoices.find((inv) => inv.id === invoiceId)!.invoice_number} - ${invoices.find((inv) => inv.id === invoiceId)!.company_name}`*/}
        {/*          : null*/}
        {/*    }*/}
        {/*    options={invoices*/}
        {/*      .filter((option) =>*/}
        {/*        `${option.invoice_number} - ${option.company_name}`*/}
        {/*          .toLowerCase()*/}
        {/*          .includes(invoiceSearch.toLowerCase()),*/}
        {/*      )*/}
        {/*      .map((option) => ({*/}
        {/*        label: `${option.invoice_number} - ${option.company_name}`,*/}
        {/*        value: option.id!,*/}
        {/*      }))}*/}
        {/*    placeholder="Choose the Invoice"*/}
        {/*  />*/}
        {/*  <input*/}
        {/*    ref={invoiceFileInputRef}*/}
        {/*    type="file"*/}
        {/*    className={styles.uploadInput}*/}
        {/*    onChange={handleInvoiceFileChange}*/}
        {/*  />*/}
        {/*  {invoiceDocId ? (*/}
        {/*    <button*/}
        {/*      type="button"*/}
        {/*      className={styles.removeButton}*/}
        {/*      onClick={() => {*/}
        {/*        setInvoiceDocId('');*/}
        {/*        setInvoiceFileName('');*/}
        {/*      }}*/}
        {/*    >*/}
        {/*      Remove*/}
        {/*    </button>*/}
        {/*  ) : (*/}
        {/*    <button*/}
        {/*      onClick={handleInvoiceUploadClick}*/}
        {/*      type="button"*/}
        {/*      className={styles.uploadButton}*/}
        {/*      disabled={lockInvoice}*/}
        {/*    >*/}
        {/*      {isInvoiceUploading ? <Spinner width={24} height={24} borderWidth={1} /> : '📄'}*/}
        {/*    </button>*/}
        {/*  )}*/}
        {/*</div>*/}
        <div className={styles.textareaWrapper}>
          <TextArea
            label="Description"
            placeholder="Fuel at Port Hercule"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
        {mode === 'payment' && !isOwner && (
          <Checkbox
            id="transaction-need-approval"
            className={styles.needApproval}
            label="Needs Owner Approval"
            checked={needApproval}
            onChange={(event) => setNeedApproval(event.target.checked)}
          />
        )}
        <div className={styles.buttons}>
          <ButtonOutlined type="button" onClick={onClose}>
            Cancel
          </ButtonOutlined>
          <Button type="submit" disabled={loading || isSubmitting || isUploading}>
            {isUploading ? 'Uploading…' : loading ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </form>

      {/* Categories are shared with budgets, so the store refetch behind this create is
          what lands the new one in the budget category list too. */}
      <CategoryModal
        open={newCategoryName !== null}
        initialName={newCategoryName ?? ''}
        onClose={() => setNewCategoryName(null)}
        onCreated={(created) => {
          setCategoryId(created.id);
          setCategorySearch(created.title);
          setSelectErrors((prev) => ({ ...prev, category: '' }));
        }}
      />
    </>
  );
};
